import clsx from 'clsx';
import {useFormik} from 'formik';
import {useState, useEffect} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import * as Yup from 'yup';
import {enqueueSnackbar} from 'notistack';
import {KeenIcon} from '@/components';

const CHANGE_PASSWORD_URL = `${import.meta.env.VITE_APP_API_URL}/change-password`;

const resetPasswordSchema = Yup.object().shape({
    new_password: Yup.string()
        .min(8, 'Minimum 8 characters')
        .max(50, 'Maximum 50 characters')
        .required('New password is required'),
    new_password_confirmation: Yup.string()
        .oneOf([Yup.ref('new_password')], "Passwords don't match")
        .required('Please confirm your new password'),
});

const ResetPasswordChange = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Parse the token from the query parameters
    const token = new URLSearchParams(location.search).get('token');

    useEffect(() => {
        if (!token) {
            enqueueSnackbar('Invalid or missing token. Redirecting to login.', {
                variant: 'solid',
                state: 'danger',
            });
            navigate('/auth/login');
        }
    }, [token, navigate]);

    const formik = useFormik({
        initialValues: {
            new_password: '',
            new_password_confirmation: '',
        },
        validationSchema: resetPasswordSchema,
        onSubmit: async (values, {setSubmitting}) => {
            setLoading(true);
            setSubmitting(true);

            try {
                const response = await fetch(CHANGE_PASSWORD_URL, {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({
                        token,
                        new_password: values.new_password,
                        new_password_confirmation: values.new_password_confirmation,
                    }),
                });

                if (response.ok) {
                    const result = await response.json();
                    enqueueSnackbar(result.message, {
                        variant: 'solid',
                        state: 'success',
                    });
                    navigate('/auth/reset-password/changed'); // Redirect to login
                } else {
                    const error = await response.json();
                    enqueueSnackbar(error.detail || 'An error occurred.', {
                        variant: 'solid',
                        state: 'danger',
                    });
                }
            } catch (error) {
                enqueueSnackbar('An unexpected error occurred. Please try again later.', {
                    variant: 'solid',
                    state: 'danger',
                });
            } finally {
                setLoading(false);
                setSubmitting(false);
            }
        },
    });

    const togglePassword = (event: React.MouseEvent) => {
        event.preventDefault();
        setShowPassword(!showPassword);
    };

    const toggleConfirmPassword = (event: React.MouseEvent) => {
        event.preventDefault();
        setShowConfirmPassword(!showConfirmPassword);
    };

    return (
        <div className="card max-w-[370px] w-full">
            <form
                className="card-body flex flex-col gap-5 p-10"
                noValidate
                onSubmit={formik.handleSubmit}
            >
                <div className="text-center mb-2.5">
                    <h3 className="text-lg font-semibold text-gray-900 leading-none mb-2.5">
                        Reset Password
                    </h3>
                    <div className="text-2sm text-gray-600">
                        Enter and confirm your new password.
                    </div>
                </div>

                <div className="flex flex-col gap-1">
                    <label className="form-label text-gray-900">New Password</label>
                    <label className="input">
                        <input
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Enter new password"
                            autoComplete="off"
                            {...formik.getFieldProps('new_password')}
                            className={clsx(
                                'form-control bg-transparent',
                                {'is-invalid': formik.touched.new_password && formik.errors.new_password},
                                {'is-valid': formik.touched.new_password && !formik.errors.new_password}
                            )}
                        />
                        <button className="btn btn-icon" onClick={togglePassword}>
                            <KeenIcon icon="eye" className={clsx('text-gray-500', {hidden: showPassword})}/>
                            <KeenIcon
                                icon="eye-slash"
                                className={clsx('text-gray-500', {hidden: !showPassword})}
                            />
                        </button>
                    </label>
                    {formik.touched.new_password && formik.errors.new_password && (
                        <span role="alert" className="text-danger text-xs mt-1">{formik.errors.new_password}</span>
                    )}
                </div>

                <div className="flex flex-col gap-1">
                    <label className="form-label text-gray-900">Confirm New Password</label>
                    <label className="input">
                        <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            placeholder="Confirm new password"
                            autoComplete="off"
                            {...formik.getFieldProps('new_password_confirmation')}
                            className={clsx(
                                'form-control bg-transparent',
                                {
                                    'is-invalid':
                                        formik.touched.new_password_confirmation &&
                                        formik.errors.new_password_confirmation,
                                },
                                {
                                    'is-valid':
                                        formik.touched.new_password_confirmation &&
                                        !formik.errors.new_password_confirmation,
                                }
                            )}
                        />
                        <button className="btn btn-icon" onClick={toggleConfirmPassword}>
                            <KeenIcon
                                icon="eye"
                                className={clsx('text-gray-500', {hidden: showConfirmPassword})}
                            />
                            <KeenIcon
                                icon="eye-slash"
                                className={clsx('text-gray-500', {hidden: !showConfirmPassword})}
                            />
                        </button>
                    </label>
                    {formik.touched.new_password_confirmation &&
                        formik.errors.new_password_confirmation && (
                            <span role="alert" className="text-danger text-xs mt-1">
                {formik.errors.new_password_confirmation}
              </span>
                        )}
                </div>

                <button
                    type="submit"
                    className="btn btn-primary flex justify-center grow"
                    disabled={loading || formik.isSubmitting}
                >
                    {loading ? 'Submitting...' : 'Submit'}
                </button>
            </form>
        </div>
    );
};

export {ResetPasswordChange};
